import React, { useState, useEffect } from 'react'
import vehicleContractPaymentService from '../../../services/vehicleContractPaymentService'
import { X, Download } from 'lucide-react'
import Alert from '../../common/Alert'
import { downloadBlob } from '../../../utils/downloadBlob.js'
import { useTranslation } from 'react-i18next';


const ViewVehicleContractPaymentModal = ({ paymentId, onClose }) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true)
  const [payment, setPayment] = useState(null)
  const [downloadingReceipt, setDownloadingReceipt] = useState(false)

  useEffect(() => {
    fetchPayment()
  }, [paymentId])

  const fetchPayment = async () => {
    try {
      setLoading(true)
      const response = await vehicleContractPaymentService.getOne(paymentId)
      setPayment(response.data.data)
    } catch (err) {
      console.error('Failed to load payment:', err)
    } finally {
      setLoading(false)
    }
  }

  const handleDownloadReceipt = async () => {
    try {
      setDownloadingReceipt(true)
      const response = await vehicleContractPaymentService.getReceiptImage(paymentId)
      await downloadBlob(response.data, `receipt_${paymentId}.png`, response.data?.type || 'image/png')
    } catch (err) {
      Alert.error('Failed to download receipt')
      console.error(err)
    } finally {
      setDownloadingReceipt(false)
    }
  }

  const formatCurrency = (value) => {
    return parseFloat(value || 0).toLocaleString('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    })
  }

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-IN', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    })
  }

  if (loading) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg shadow-lg p-8 w-full max-w-2xl">
          <div className="flex justify-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500"></div>
          </div>
        </div>
      </div>
    )
  }

  if (!payment) {
    return null
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-lg w-full max-w-2xl max-h-[90vh] overflow-auto">
        {/* Modal Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 sticky top-0 bg-white">
          <h2 className="text-xl font-bold text-secondary">Payment Details</h2>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Modal Body */}
        <div className="p-6">
          <div className="grid grid-cols-2 sm:grid-cols-2 gap-2 sm:gap-6">
            {/* Payment ID */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Payment ID</label>
              <p className="text-gray-900 font-semibold">{payment.payment_id}</p>
            </div>

            {/* Amount */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Amount</label>
              <p className="text-gray-900 font-semibold text-green-600">{formatCurrency(payment.amount)}</p>
            </div>

            {/* Payment Date */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Payment Date</label>
              <p className="text-gray-900">{formatDate(payment.payment_date)}</p>
            </div>

            {/* Payment Mode */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Payment Mode</label>
              <p className="text-gray-900">{payment.payment_mode || 'N/A'}</p>
            </div>

            {/* Reference Number */}
            <div className="sm:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-2">Reference Number</label>
              <p className="text-gray-900">{payment.reference_number || 'N/A'}</p>
            </div>

            {/* Remarks */}
            {payment.remarks && (
              <div className="sm:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">Remarks</label>
                <p className="text-gray-900">{payment.remarks}</p>
              </div>
            )}

            {/* Created By */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">{t('common.createdBy')}</label>
              <p className="text-gray-900">{payment.created_by}</p>
            </div>

            {/* Created At */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Created At</label>
              <p className="text-gray-900 text-sm">{new Date(payment.created_at).toLocaleString('en-IN')}</p>
            </div>

            {/* Updated By */}
            {payment.updated_by && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Updated By</label>
                <p className="text-gray-900">{payment.updated_by}</p>
              </div>
            )}

            {/* Updated At */}
            {payment.updated_at && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Updated At</label>
                <p className="text-gray-900 text-sm">{new Date(payment.updated_at).toLocaleString('en-IN')}</p>
              </div>
            )}
          </div>

          {/* Receipt Image */}
          {payment.has_receipt_image && (
            <div className="mt-6 border-t border-gray-200 pt-6">
              <label className="block text-sm font-medium text-gray-700 mb-4">Receipt Image</label>
              <button
                onClick={handleDownloadReceipt}
                disabled={downloadingReceipt}
                className="inline-flex items-center gap-2 px-4 py-2 bg-blue-500 hover:bg-blue-600 disabled:opacity-50 text-white rounded-lg transition"
              >
                <Download className="w-4 h-4" />
                {downloadingReceipt ? 'Downloading...' : 'Download Receipt'}
              </button>
            </div>
          )}

          {/* Modal Actions */}
          <div className="mt-8">
            <button
              onClick={onClose}
              className="w-full px-6 py-2 border border-gray-200 text-gray-700 rounded-lg hover:bg-gray-50 transition"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

export default ViewVehicleContractPaymentModal
